import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ORDER_FLOW, SHORT_STEP, statusLabel, statusTone } from '../lib/format.js'
import { useLiveVersion } from '../lib/live.jsx'

// ------------------------------------------------------------------ toasts
const ToastContext = createContext(null)

export function ToastProvider ({ children }) {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const push = useCallback((kind, message) => {
    const id = ++idRef.current
    setToasts(t => [...t, { id, kind, message }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), kind === 'error' ? 7000 : 4500)
  }, [])

  const value = useMemo(() => ({
    ok: m => push('ok', m),
    error: m => push('error', m),
    info: m => push('info', m),
    warn: m => push('warn', m)
  }), [push])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack">
        {toasts.map(t => (
          <div key={t.id} className={`toast ${t.kind}`} onClick={() => setToasts(x => x.filter(i => i.id !== t.id))}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)

// ----------------------------------------------------------------- confirm
const ConfirmContext = createContext(null)

/**
 * A promise-shaped "are you sure?".
 *
 *   if (!await confirm({ title: 'Suspend this hatchery?', ... })) return
 *
 * Every action that moves money, changes who can sign in, changes what farmers
 * see, or cannot be undone from the same screen goes through this. The dialog
 * states the consequence rather than asking an abstract question, and `facts`
 * shows the actual values being committed — the operator is usually reading
 * them off a phone call.
 */
export function ConfirmProvider ({ children }) {
  const [pending, setPending] = useState(null)

  const confirm = useCallback(options => new Promise(resolve => {
    const opts = typeof options === 'string' ? { title: options } : (options || {})
    setPending({
      title: 'Are you sure?',
      confirmLabel: 'Yes, continue',
      cancelLabel: 'Cancel',
      tone: '',
      ...opts,
      resolve
    })
  }), [])

  const settle = value => {
    setPending(current => {
      current?.resolve(value)
      return null
    })
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <Modal title={pending.title} onClose={() => settle(false)}>
          <div className="confirm">
            {pending.body && <p className="confirm-body">{pending.body}</p>}

            {pending.facts?.length > 0 && (
              <dl className="confirm-facts">
                {pending.facts.filter(Boolean).map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            )}

            {pending.warning && (
              <div className={`alert ${pending.tone === 'danger' ? 'error' : 'warn'}`}>
                <div>{pending.warning}</div>
              </div>
            )}

            <div className="btnrow confirm-actions">
              <button
                className={`btn ${pending.tone === 'danger' ? 'danger' : 'primary'}`}
                onClick={() => settle(true)}
                autoFocus={pending.tone !== 'danger'}
              >
                {pending.confirmLabel}
              </button>
              <button className="btn" onClick={() => settle(false)} autoFocus={pending.tone === 'danger'}>
                {pending.cancelLabel}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  )
}

/** Outside a provider (tests, isolated renders) nothing should block. */
export const useConfirm = () => useContext(ConfirmContext) || (() => Promise.resolve(true))

// ------------------------------------------------------------------ loading
export function Spinner ({ label = 'Loading…' }) {
  return <div className="loading"><span className="spin" />{label}</div>
}

export function Empty ({ children = 'Nothing here yet.' }) {
  return <div className="empty">{children}</div>
}

export function ErrorBox ({ error, onRetry }) {
  if (!error) return null
  return (
    <div className="alert error">
      <div>
        <b>Could not load this screen</b>
        {error.message}
        {onRetry && <div className="btnrow" style={{ marginTop: 8 }}>
          <button className="btn sm" onClick={onRetry}>Try again</button>
        </div>}
      </div>
    </div>
  )
}

/**
 * Small data-fetching helper: keeps loading/error/reload wiring out of pages.
 *
 * Pass `{ live: 'orders' }` (or a list) and the screen re-fetches itself the
 * moment the API says that topic moved — without the spinner flash, because a
 * refresh that already has data on screen only sets `refreshing`.
 */
export function useAsync (fn, deps = [], opts = {}) {
  const live = useLiveVersion(opts.live)
  const [state, setState] = useState({ data: null, error: null, loading: true, refreshing: false })
  const [nonce, setNonce] = useState(0)
  const fnRef = useRef(fn)
  fnRef.current = fn
  const hasData = useRef(false)

  useEffect(() => {
    let alive = true
    setState(s => (hasData.current
      ? { ...s, refreshing: true }
      : { ...s, loading: true, error: null }))

    Promise.resolve(fnRef.current())
      .then(data => {
        if (!alive) return
        hasData.current = true
        setState({ data, error: null, loading: false, refreshing: false })
      })
      .catch(error => {
        if (!alive) return
        // A background refresh that fails must not blank a screen the user is
        // reading — keep what is there and try again on the next ping.
        setState(s => (hasData.current
          ? { ...s, refreshing: false }
          : { data: null, error, loading: false, refreshing: false }))
      })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, live])

  return { ...state, reload: () => setNonce(n => n + 1) }
}

// -------------------------------------------------------------------- shell
export function Page ({ title, subtitle, crumb, actions, children, size }) {
  return (
    <div className={`wrap ${size || ''}`}>
      <div className="pagehead">
        <div className="pagehead-main">
          {crumb && <div className="crumb">{crumb}</div>}
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="spacer" />
        {actions && <div className="btnrow">{actions}</div>}
      </div>
      {children}
    </div>
  )
}


export function Card ({ title, actions, children, padded = true, className = '' }) {
  return (
    <div className={`card ${className}`}>
      {(title || actions) && (
        <div className="cardhead">
          <h2>{title}</h2>
          <div className="spacer" />
          {actions}
        </div>
      )}
      {padded ? <div className="body">{children}</div> : children}
    </div>
  )
}

export function Stat ({ value, label, sub, tone }) {
  return (
    <div className={`stat ${tone || ''}`}>
      <div className="n">{value}</div>
      <div className="k">{label}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  )
}

export function Badge ({ tone = 'muted', children }) {
  return <span className={`badge ${tone}`}>{children}</span>
}

export function StatusBadge ({ status }) {
  return <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>
}

export function KV ({ items }) {
  return (
    <div className="kv">
      {items.filter(Boolean).map((it, i) => (
        <div key={i}>
          <div className="k">{it.k}</div>
          <div className={`v ${it.big ? 'big' : ''}`}>{it.v ?? '—'}</div>
        </div>
      ))}
    </div>
  )
}

// -------------------------------------------------------------------- forms
export function Field ({ label, required, hint, children, wide }) {
  return (
    <label className="f" style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <span>{label}{required && <span className="req"> *</span>}</span>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </label>
  )
}

export function Select ({ options, value, onChange, placeholder, ...rest }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)} {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => {
        const val = typeof o === 'string' ? o : o.value
        const lab = typeof o === 'string' ? o : o.label
        return <option key={val} value={val}>{lab}</option>
      })}
    </select>
  )
}

/** Submit button that shows in-flight state, so a slow POST cannot be double-fired. */
export function SubmitButton ({ busy, children, className = 'btn primary', ...rest }) {
  return (
    <button className={className} disabled={busy} {...rest}>
      {busy ? <><span className="spin small" /> Working…</> : children}
    </button>
  )
}

// A confirm opens on top of an action's own modal, so Escape has to close the
// top one only — otherwise one key press also throws away the form underneath.
const modalStack = []

export function Modal ({ title, onClose, children, wide }) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const token = {}
    modalStack.push(token)
    const onKey = e => {
      if (e.key !== 'Escape') return
      if (modalStack[modalStack.length - 1] !== token) return
      closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      const i = modalStack.indexOf(token)
      if (i !== -1) modalStack.splice(i, 1)
    }
    // mount/unmount only: re-running would reorder the stack on every render
  }, [])

  return (
    <div className="modal-back" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------- order rail
export function StatusRail ({ status }) {
  const failed = status === 'sample_failed'
  const cancelled = status === 'cancelled'
  const idx = ORDER_FLOW.indexOf(status)

  return (
    <div className="rail">
      {ORDER_FLOW.map((s, i) => {
        let cls = ''
        if (failed) {
          if (i < 3) cls = 'done'
          else if (i === 3) cls = 'fail'
        } else if (cancelled) {
          cls = i === 0 ? 'done' : ''
        } else if (i < idx) cls = 'done'
        else if (i === idx) cls = 'now'
        return (
          <div key={s} className={`step ${cls}`}>
            {failed && i === 3 ? 'Failed' : SHORT_STEP[s]}
          </div>
        )
      })}
    </div>
  )
}

export function Timeline ({ events = [], fmt }) {
  if (!events.length) return <Empty>No activity recorded yet.</Empty>
  return (
    <ul className="timeline">
      {[...events].reverse().map((e, i) => (
        <li key={e._id || i}>
          <div className="ev">{e.event}</div>
          <div className="meta">{fmt(e.at)} · {e.actorLabel || 'system'}</div>
          {e.notes && <div className="note">{e.notes}</div>}
        </li>
      ))}
    </ul>
  )
}


// ------------------------------------------------------------ copy & sharing
/** Clipboard with a fallback for the http:// origins a demo laptop runs on. */
export async function copyText (value) {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    try {
      const el = document.createElement('textarea')
      el.value = value
      el.setAttribute('readonly', '')
      el.style.position = 'fixed'
      el.style.opacity = '0'
      document.body.appendChild(el)
      el.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(el)
      return ok
    } catch {
      return false
    }
  }
}

export function CopyButton ({ value, children = 'Copy', className = 'btn sm', told = 'Copied to clipboard' }) {
  const toast = useToast()
  const [done, setDone] = useState(false)

  const run = async () => {
    const ok = await copyText(value)
    if (!ok) return toast?.error('Could not reach the clipboard — select the text and copy it by hand')
    setDone(true)
    toast?.ok(told)
    setTimeout(() => setDone(false), 1800)
  }
  return <button type="button" className={className} onClick={run}>{done ? '✓ Copied' : children}</button>
}

/** A read-only value the operator is meant to pass on — link, username, password. */
export function CopyRow ({ label, value, hint, mono = true, wrap: allowWrap = false }) {
  return (
    <div className="copyrow">
      {label && <div className="copyrow-k">{label}</div>}
      <div className="copyrow-v">
        <span className={`${mono ? 'mono' : ''} ${allowWrap ? 'wrapany' : 'clip'}`}>{value}</span>
        <CopyButton value={value} />
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  )
}

// -------------------------------------------------------------------- wizard
export function StepNav ({ steps, current, furthest = 0, onGo }) {
  return (
    <ol className="stepnav">
      {steps.map((s, i) => {
        const state = i === current ? 'now' : i < furthest ? 'done' : ''
        const reachable = i <= furthest
        return (
          <li key={s.key || i} className={`stepnav-item ${state}`}>
            <button type="button" disabled={!reachable} onClick={() => reachable && onGo?.(i)}>
              <span className="dot">{i < furthest && i !== current ? '✓' : i + 1}</span>
              <span className="lbl">
                <b>{s.title}</b>
                {s.hint && <small>{s.hint}</small>}
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

// ----------------------------------------------------------------- checklist
export function Checklist ({ items }) {
  return (
    <ul className="checklist">
      {items.filter(Boolean).map((it, i) => (
        <li key={i} className={it.done ? 'done' : ''}>
          <span className="tick">{it.done ? '✓' : i + 1}</span>
          <div>
            <b>{it.title}</b>
            {it.body && <p>{it.body}</p>}
            {!it.done && it.action}
          </div>
          {it.done && <span className="badge good">Done</span>}
        </li>
      ))}
    </ul>
  )
}

export function Skeleton ({ rows = 3 }) {
  return (
    <div className="skeleton">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="sk-line" style={{ width: `${92 - i * 11}%` }} />)}
    </div>
  )
}
